/**
 * Cumulative antibiogram types and API calls (docs/phase2, AN-xx).
 *
 * The payloads here carry counts only — no isolate, hospital number or record
 * ID ever appears in an analysis result, which is what lets the same payload
 * be published later.
 */

import { request } from "./api";

export type DedupScope = "patient_species" | "patient_species_specimen";
export type Numerator = "s_only" | "s_and_i";

export interface AnalysisFilters {
  date_from?: string | null;
  date_to?: string | null;
  team?: string | null;
  specimen_type?: string | null;
  ward_code?: string | null;
  location_type?: string | null;
  organism_code?: string | null;
  include_drafts?: boolean;
}

export interface DedupRule {
  scope: DedupScope;
  /** Days after which a later isolate counts again. Null = never. */
  episode_days: number | null;
}

export interface AntibiogramParams {
  suppression_threshold: number;
  numerator: Numerator;
  min_organism_isolates: number;
}

export interface AnalysisRequest {
  title?: string;
  filters?: AnalysisFilters;
  dedup?: Partial<DedupRule>;
  antibiogram?: Partial<AntibiogramParams>;
}

export interface DedupSummary {
  kept: number;
  removed: number;
  total: number;
  with_patient_key: number;
  without_organism: number;
  /** Share of isolates that carried a patient key (AN-04). */
  coverage: number;
}

export interface AnalysisPreview {
  dedup: DedupSummary;
  distinct_patients: number;
  organisms: number;
  antibiotics: number;
  suppressed_cells: number;
  total_cells: number;
}

export interface Cell {
  n_tested: number;
  n_s: number;
  n_i: number;
  n_r: number;
  /** Null when too few isolates were tested to report a percentage. */
  percent_susceptible: number | null;
  suppressed: boolean;
}

export interface MatrixRow {
  organism_code: string;
  n_isolates: number;
  cells: Record<string, Cell>;
}

export interface Matrix {
  params: AntibiogramParams;
  organisms: string[];
  antibiotics: string[];
  total_isolates: number;
  rows: Record<string, MatrixRow>;
}

export interface PhenotypeResult {
  code: string;
  label: string;
  /** True when this is a screening inference, not a confirmed result. */
  is_proxy: boolean;
  note: string;
  n_eligible: number;
  n_positive: number;
  percent: number | null;
  suppressed: boolean;
}

export interface AnalysisResult {
  antibiogram: Matrix;
  phenotypes: PhenotypeResult[];
  dedup: DedupSummary;
  distinct_patients: number;
}

export interface AnalysisRun {
  id: string;
  user_id: string;
  title: string;
  period_start: string | null;
  period_end: string | null;
  parameters: Record<string, unknown>;
  isolates_considered: number;
  isolates_included: number;
  dedup_coverage: number;
  distinct_patients: number;
  result_hash: string | null;
  created_at: string;
}

export interface AnalysisRunDetail extends AnalysisRun {
  result: AnalysisResult;
}

export const DEFAULT_PARAMS: AntibiogramParams = {
  // CLSI M39: below 30 isolates a percentage is too unstable to report.
  suppression_threshold: 30,
  numerator: "s_only",
  min_organism_isolates: 0,
};

export const DEFAULT_DEDUP: DedupRule = {
  scope: "patient_species",
  episode_days: null,
};

export const analysis = {
  preview: (body: AnalysisRequest) =>
    request<AnalysisPreview>("/analysis/preview", { method: "POST", body }),

  createRun: (body: AnalysisRequest) =>
    request<AnalysisRunDetail>("/analysis/runs", { method: "POST", body }),

  listRuns: () => request<AnalysisRun[]>("/analysis/runs"),

  getRun: (id: string) => request<AnalysisRunDetail>(`/analysis/runs/${id}`),

  /** Fetch the CSV with the access token attached and hand it to the browser. */
  async download(id: string, filename: string): Promise<void> {
    const response = await request<Response>(`/analysis/runs/${id}/export`, { raw: true });
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  },
};

/** How a cell should read in the table: "82.4%", "(12)", or blank. */
export function formatCell(cell: Cell | undefined): string {
  if (!cell || cell.n_tested === 0) return "";
  if (cell.percent_susceptible === null) return `(${cell.n_tested})`;
  return `${cell.percent_susceptible}%`;
}

/**
 * Colour band for a cell. Resistance is the thing a reader is scanning for, so
 * low %S is what stands out.
 */
export function cellTone(cell: Cell | undefined): "none" | "low" | "mid" | "high" {
  if (!cell || cell.percent_susceptible === null) return "none";
  if (cell.percent_susceptible < 50) return "low";
  if (cell.percent_susceptible < 80) return "mid";
  return "high";
}
